import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import AdminLayout from './AdminLayout';

const REASON_LABELS = {
  purchase: 'Purchase', sale: 'Sale', loss: 'Loss / waste',
  production: 'Production', deproduction: 'De-production',
  adjustment: 'Adjustment', restock: 'Restock', restock_reversal: 'Restock reversal',
  cancel_restock: 'Order canceled (restock)', customer_return: 'Customer return', return: 'Supplier return',
};

/**
 * Inventory movements — the audit log of every stock change (in/out) for
 * raw materials AND finished products. The ledger for physical inventory.
 */
const InventoryMovementsPage = () => {
  const [movements, setMovements] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [itemFilter, setItemFilter] = useState(''); // 'ing:<id>' | 'prod:<id>'
  const [locFilter, setLocFilter] = useState(''); // locationId | 'global' | ''
  const user = getAuth().currentUser;

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const db = getFirestore();
      const [movSnap, locSnap] = await Promise.all([
        getDocs(query(collection(db, 'users', user.uid, 'inventoryMovements'), orderBy('createdAt', 'desc'), limit(300))),
        getDocs(collection(db, 'users', user.uid, 'locations')),
      ]);
      setMovements(movSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLocations(locSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = movements.filter(m => {
    if (kindFilter !== 'all' && m.kind !== kindFilter) return false;
    if (itemFilter) {
      const [prefix, id] = itemFilter.split(':');
      const kind = prefix === 'prod' ? 'product' : 'ingredient';
      if (m.kind !== kind || m.refId !== id) return false;
    }
    if (locFilter === 'global' && m.locationId) return false;
    if (locFilter && locFilter !== 'global' && m.locationId !== locFilter) return false;
    if (fromDate) {
      const from = new Date(`${fromDate}T00:00:00`);
      const at = m.createdAt?.toDate?.();
      if (!at || at < from) return false;
    }
    if (toDate) {
      const to = new Date(`${toDate}T23:59:59.999`);
      const at = m.createdAt?.toDate?.();
      if (!at || at > to) return false;
    }
    return true;
  });

  // Distinct items for the picker (grouped material/product)
  const itemOptions = [];
  const seen = new Set();
  for (const m of movements) {
    const key = `${m.kind === 'product' ? 'prod' : 'ing'}:${m.refId}`;
    if (!m.refId || seen.has(key)) continue;
    seen.add(key);
    itemOptions.push({ key, label: m.name || m.refId, kind: m.kind });
  }
  itemOptions.sort((a, b) => a.label.localeCompare(b.label));
  const locName = (id) => locations.find(l => l.id === id)?.companyName || (id ? 'Location' : 'Global');

  return (
    <AdminLayout title="Inventory Movements">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0 font-weight-bold">Inventory Movements</h5>
          <small className="text-muted">Every stock in/out — materials and products, with before → after</small>
        </div>
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
          <input type="date" className="form-control form-control-sm" style={{ width: 140 }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} title="From" />
          <input type="date" className="form-control form-control-sm" style={{ width: 140 }} value={toDate} onChange={(e) => setToDate(e.target.value)} title="To" />
          <select className="form-control form-control-sm" style={{ width: 210 }} value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}>
            <option value="">All items</option>
            {itemOptions.some(o => o.kind !== 'product') && <optgroup label="Raw materials">{itemOptions.filter(o => o.kind !== 'product').map(o => <option key={o.key} value={o.key}>{o.label}</option>)}</optgroup>}
            {itemOptions.some(o => o.kind === 'product') && <optgroup label="Products">{itemOptions.filter(o => o.kind === 'product').map(o => <option key={o.key} value={o.key}>{o.label}</option>)}</optgroup>}
          </select>
          <select className="form-control form-control-sm" style={{ width: 180 }} value={locFilter} onChange={(e) => setLocFilter(e.target.value)}>
            <option value="">All locations</option>
            {movements.some(m => !m.locationId) && <option value="global">Global (no location)</option>}
            {locations.map(l => <option key={l.id} value={l.id}>{l.companyName}</option>)}
          </select>
          <select className="form-control form-control-sm" style={{ width: 170 }} value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <option value="all">All (materials + products)</option>
            <option value="ingredient">Raw materials</option>
            <option value="product">Products</option>
          </select>
          <button className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
            <i className="fas fa-rotate"></i>
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>When</th><th>Kind</th><th>Item</th><th>Location</th><th>Reason</th>
                <th className="text-end">Change</th><th className="text-end">Before → After</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-4"><div className="spinner-border spinner-border-sm" style={{ color: '#ff6b35' }} /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-4 text-muted">No movements yet — they write on purchases, sales, losses, and stock adjustments.</td></tr>
              ) : filtered.map(m => (
                <tr key={m.id}>
                  <td className="text-nowrap"><small>{m.createdAt?.toDate ? m.createdAt.toDate().toLocaleString() : '—'}</small></td>
                  <td>
                    <span className={`badge ${m.kind === 'product' ? 'badge-info' : 'badge-warning'}`}>
                      {m.kind === 'product' ? 'Product' : 'Material'}
                    </span>
                  </td>
                  <td className="fw-semibold">{m.name}</td>
                  <td><small>{locName(m.locationId)}</small></td>
                  <td><span className="badge badge-light border">{REASON_LABELS[m.reason] || m.reason}</span></td>
                  <td className={`text-end fw-bold text-nowrap ${m.delta < 0 ? 'text-danger' : 'text-success'}`}>
                    {m.delta > 0 ? '+' : ''}{m.delta % 1 === 0 ? m.delta : Number(m.delta).toFixed(2)} {m.unit || ''}
                  </td>
                  <td className="text-end text-muted text-nowrap"><small>{m.before != null ? (Math.round(m.before * 10000) / 10000) : '—'} → {m.after != null ? (Math.round(m.after * 10000) / 10000) : '—'}</small></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default InventoryMovementsPage;
